import { AppLayout } from '@/components/layout/AppLayout'
import { AtlasPage } from '@/components/ui/AtlasPage'
import { PageTitle } from '@/components/ui/PageTitle'
import { Separator } from '@/components/ui/Separator'
import { PageNumber } from '@/components/ui/PageNumber'
import { Button } from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { Link, router } from '@inertiajs/react'
import { PLACEHOLDER_ICONS } from '@/lib/icons'
import { useState, useEffect } from 'react'

interface Plant {
  id: number
  name: string
  species_name: string | null
  status: string | null
  status_pl: string | null
  cover_photo_url: string | null
  thumbnail_url: string | null
  species_illustration_url: string | null
  needs_watering: boolean
  days_until_watering: number | null
}

interface Props {
  plants: Plant[]
  filters: { q?: string; status?: string }
}

const statusOptions = [
  { value: '', label: 'Wszystkie' },
  { value: 'healthy', label: 'Zdrowe' },
  { value: 'needs_attention', label: 'Wymaga uwagi' },
  { value: 'sick', label: 'Chore' },
  { value: 'dormant', label: 'Uśpione' },
]

function PlantsIndex({ plants, filters }: Props) {
  const [query, setQuery] = useState(filters.q || '')

  useEffect(() => {
    const timeout = setTimeout(() => {
      router.get('/plants', { q: query || undefined, status: filters.status || undefined }, { preserveState: true, replace: true })
    }, 300)
    return () => clearTimeout(timeout)
  }, [query])

  const handleStatusFilter = (status: string) => {
    router.get('/plants', { q: filters.q || undefined, status: status || undefined }, { preserveState: true, replace: true })
  }

  return (
    <AtlasPage>
      <PageTitle title="Rośliny" subtitle="Katalog kolekcji" />
      <Separator />

      {/* Search */}
      <div className="mb-md">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Szukaj rośliny..."
          className="w-full border border-frame bg-paper-dark px-md py-sm font-body text-sm text-ink placeholder-frame/60 focus:border-botanical focus:outline-none"
        />
      </div>

      {/* Status filters */}
      <div className="flex flex-wrap items-center justify-center gap-xs mb-md">
        {statusOptions.map((opt) => (
          <button
            key={opt.value}
            onClick={() => handleStatusFilter(opt.value)}
            className={`px-sm py-xs font-label text-xs tracking-wide border transition-colors ${
              (filters.status || '') === opt.value
                ? 'border-botanical bg-botanical text-white'
                : 'border-frame text-frame hover:bg-paper-dark'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Grid */}
      {plants.length > 0 ? (
        <div className="grid grid-cols-1 gap-md sm:grid-cols-2 lg:grid-cols-3">
          {plants.map((plant) => (
            <Link
              key={plant.id}
              href={`/plants/${plant.id}`}
              className="group block border border-frame/30 bg-paper-dark/50 p-sm transition-shadow hover:shadow-hover"
            >
              {(() => {
                const imgUrl = plant.cover_photo_url || plant.species_illustration_url
                return imgUrl ? (
                  <div className="aspect-square w-full overflow-hidden mb-sm" style={{ border: '1px solid var(--desk)' }}>
                    <img src={imgUrl} alt={plant.name} className="h-full w-full object-contain" />
                  </div>
                ) : (
                  <div className="aspect-square w-full flex items-center justify-center mb-sm" style={{ background: 'var(--desk)', opacity: 0.5 }}>
                    <PLACEHOLDER_ICONS.plant.icon weight="regular" size={48} className="text-frame/40" />
                  </div>
                )
              })()}
              <h3 className="font-display text-sm uppercase tracking-wider text-ink group-hover:text-botanical">
                {plant.name}
              </h3>
              {plant.species_name && (
                <p className="text-xs italic text-frame mt-xs">{plant.species_name}</p>
              )}
              <div className="mt-xs">
                <StatusBadge status={plant.status} />
              </div>
              {plant.needs_watering && (
                <p className="mt-xs text-xs text-danger font-label">Wymaga podlania!</p>
              )}
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-xl">
          <p className="text-frame italic font-label">Brak roślin w kolekcji.</p>
          <p className="text-frame/60 text-xs mt-xs">Dodaj swoją pierwszą roślinę, aby rozpocząć.</p>
        </div>
      )}

      <Separator />
      <div className="flex justify-center">
        <Button href="/plants/new">Dodaj roślinę</Button>
      </div>

      <PageNumber number={2} />
    </AtlasPage>
  )
}

PlantsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
export default PlantsIndex
